import { useEffect } from 'react';

interface CollectionItem {
  id: string;
  title: string;
  categoryId: string;
  cover: string;
  duration: string;
  views: string;
  date: string;
  tags: string[];
  summary: string;
}

interface CollectionModalProps {
  item: CollectionItem | null;
  categoryName: string;
  onClose: () => void;
}

export default function CollectionModal({ item, categoryName, onClose }: CollectionModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (item) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div
        className="absolute inset-0 bg-foreground-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-lg border border-background-200">
        <div className="relative w-full aspect-[8/5] md:aspect-[16/9]">
          <img
            src={item.cover}
            alt={`${item.title} 画面预览`}
            className="w-full h-full object-cover object-top rounded-t-lg"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent rounded-t-lg"></div>

          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/60 text-background-50 hover:bg-foreground-950/80 transition-colors cursor-pointer"
            aria-label="关闭预览"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-label mb-3">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {categoryName}
            </span>
            <h3 className="font-heading font-bold text-lg md:text-2xl text-background-50 leading-snug">
              {item.title}
            </h3>
          </div>

          {item.duration !== '00:00' && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs font-label">
              <i className="ri-time-line" aria-hidden="true"></i>
              {item.duration}
            </span>
          )}
        </div>

        <div className="p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {item.views} 次浏览
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              入库于 {item.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-hd-line" aria-hidden="true"></i>
              高清可用
            </span>
          </div>

          <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">{item.summary}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-md bg-background-100 border border-background-200">
            <p className="flex items-start gap-2.5 text-xs md:text-sm leading-relaxed text-foreground-600">
              <i className="ri-information-line text-primary-600 text-base mt-0.5" aria-hidden="true"></i>
              <span>
                该合集为站内归档条目，用于整理公开流传的现场影像资料。如需查看完整片段或反馈内容问题，请通过页面底部的联系表单告诉我们。
              </span>
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href="#contact"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              申请查看完整片段
            </a>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-sm font-label whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
            >
              返回合集列表
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}